import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { API_BASE_URL } from '../apiBase';

export default function ExportOtpDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Send OTP when dialog opens
  useEffect(() => {
    if (open) {
      sendOtp();
    } else {
      // Reset state when closed
      setOtpId(null);
      setOtp('');
      setError('');
      setMessage('');
      setLoading(false);
      setVerifying(false);
    }
  }, [open]);

  const sendOtp = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/export-otp/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }
      setOtpId(data.otpId);
      setMessage(data.message || 'OTP sent to Superadmin.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!otp) return;
    setVerifying(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/export-otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ otpId, otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Verification failed');
      }
      // Success
      onSuccess();
      onClose(); // Close dialog
    } catch (e) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export Authorization</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Sending OTP to Superadmin...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please enter the OTP sent to the Superadmin's email to proceed with the export.
            </Typography>
            
            {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              autoFocus
              label="OTP Code"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={verifying || !otpId}
              type="text" // OTP is numeric usually but text is safer for leading zeros
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={verifying}>Cancel</Button>
        <Button 
          onClick={handleVerify} 
          variant="contained" 
          disabled={loading || verifying || !otpId || !otp}
        >
          {verifying ? 'Verifying...' : 'Verify & Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
